import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { handleOnSettled } from '@utils/formHelpers';
import { useCreateExternalSchedule } from '@services/schedules/create';
import { useUpdateExternalSchedule } from '@services/schedules/update';
import {
  ConcreteDirectOrder,
  ConcreteDirectOrderAdditiveItem,
  ExternalScheduleResp,
  ScheduleNote,
} from '@services/schedules/types';
import { Note, RateType } from '@root/globalTypes';
import { getHours, getMinutes, isDate } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useErrors } from '@hooks/errors';
import { rateTypes } from '@components/schedules/external/inputs/concreteDirect/constants';

export type DeliveryRate = 'hours' | 'mins';

interface ExternalScheduleFormValues {
  date?: Date;
  time?: Date;
  note: string;
  extraComment?: string;
  confirmed: boolean;
  taskId?: string;
  externalContactId?: string;
  externalSiteId?: string;
  additionalInformation?: string;
  rateType: RateType;
  quantityRate?: string;
  additive?: ConcreteDirectOrderAdditiveItem;
  id?: string[];
  itemExternalId?: string;
  pourMethod?: string;
  pourElement?: string;
  isExactQuantity?: boolean;
  slump?: string;
  localStartDatetime?: string;
  spacingInMinutes?: string;
  quantity?: string;
  qtyPerHour?: string;
}

export const defaultValues: ExternalScheduleFormValues = {
  date: undefined,
  time: undefined,
  note: '',
  extraComment: '',
  confirmed: true,
  taskId: '',
  externalContactId: '',
  externalSiteId: '',
  additionalInformation: '',
  id: [],
  itemExternalId: '',
  pourMethod: '',
  pourElement: '',
  isExactQuantity: false,
  slump: '',
  localStartDatetime: '',
  spacingInMinutes: '',
  quantity: '',
  qtyPerHour: '',
  rateType: 'SPACING_IN_MINUTES',
  quantityRate: '',
  additive: { id: [], itemExternalId: '', quantity: '' },
};

export const useExternalScheduleForm = ({
  successHandler,
  concreteDirect,
  jobId,
  taskId,
  scheduleId,
  note,
}: {
  successHandler: () => void;
  concreteDirect?: boolean;
  jobId?: string;
  taskId?: string;
  scheduleId?: string;
  note?: Note;
  extraComment?: string;
}) => {
  const { t } = useTranslation('app', { keyPrefix: 'forms' });
  const handleErrors = useErrors();

  const concreteDirectFormSchema = () => ({
    additionalInformation: z.string(),
    externalSiteId: concreteDirect
      ? z.string().min(1, { message: t('externalSiteId.required') })
      : z.string(),
    itemExternalId: z.string(),
    id: concreteDirect
      ? z.array(z.string()).min(1, { message: t('itemExternalId.required') })
      : z.array(z.string()),
    pourMethod: concreteDirect
      ? z.string().min(1, { message: t('pourMethod.required') })
      : z.string(),
    pourElement: concreteDirect
      ? z.string().min(1, { message: t('pourElement.required') })
      : z.string(),
    isExactQuantity: z.boolean(),
    slump: z.string(),
    localStartDatetime: z.string(),
    quantity: concreteDirect ? z.string().min(1, { message: t('quantity.required') }) : z.string(),
    rateType: z.enum(rateTypes),
    quantityRate: concreteDirect
      ? z.string().min(1, { message: t('quantity.required') })
      : z.string(),
    additive: z
      .object({
        itemExternalId: z.string(),
        id: z.array(z.string()),
        quantity: z.string(),
      })
      .refine(
        ({ itemExternalId, quantity }) => (concreteDirect && itemExternalId ? quantity : true),
        {
          message: t('quantity.required'),
          path: ['quantity'],
        },
      ),
  });

  const formSchema = z.object({
    date: z.custom<Date>((v) => isDate(v), { message: t('date.required') }),
    time: z.custom<Date>((v) => isDate(v), { message: t('time.required') }),
    note: z.string(),
    extraComment: z.string(),
    confirmed: z.boolean(),
    taskId: z.string(),
    externalContactId: z.string(),
    ...concreteDirectFormSchema(),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  const callback = (resp: ExternalScheduleResp) => {
    handleOnSettled<ExternalScheduleResp>(resp, successHandler, () =>
      handleErrors(resp.errors, (key, message) =>
        form.setError(key as keyof z.infer<typeof formSchema>, { message }),
      ),
    );
  };

  const create = useCreateExternalSchedule({ callback });
  const reschedule = useUpdateExternalSchedule({ callback });

  const onSubmit = async ({
    date,
    time,
    confirmed,
    note: content,
    extraComment,
    externalContactId,
    externalSiteId,
    additionalInformation,
    id,
    isExactQuantity,
    itemExternalId,
    additive,
    pourElement,
    pourMethod,
    rateType,
    quantityRate,
    quantity,
    slump,
  }: z.infer<typeof formSchema>) => {
    if (!jobId || !taskId) return;
    const startsAt = date;
    startsAt.setHours(getHours(time), getMinutes(time));
    const cdApiOrder: ConcreteDirectOrder = {
      jobId,
      externalSiteId,
      additionalInformation,
      orderItems: [
        {
          id: id[0],
          isExactQuantity,
          itemExternalId,
          localStartDatetime: startsAt.toISOString(),
          orderItemAdditions: additive.itemExternalId ? [{ ...additive, id: additive.id[0] }] : [],
          pourElement,
          pourMethod,
          qtyPerHour: rateType === 'QTY_PER_HOUR' ? quantityRate : undefined,
          quantity,
          slump,
          spacingInMinutes: rateType === 'SPACING_IN_MINUTES' ? quantityRate : undefined,
        },
      ],
    };

    const noteObject = content
      ? ({
          id: note?.id,
          content,
          referableId: taskId,
          referableType: 'Task',
        } as ScheduleNote)
      : undefined;

    const data = {
      jobId,
      scheduleId,
      schedule: {
        taskId,
        startsAt,
        confirmed,
        externalContactId,
        extraComment,
        cdApiOrder: concreteDirect ? cdApiOrder : undefined,
      },
      note: noteObject,
    };

    if (scheduleId) reschedule.mutate(data);
    else create.mutate(data);
  };

  return {
    form,
    onSubmit,
    locked: create.isLoading || reschedule.isLoading,
  };
};
